import { Box, Collapse, Typography, alpha } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

/** Vertical tree rail under a parent node */
export const TreeChildren = ({ color, children }) => (
  <Box
    sx={{
      position: 'relative',
      ml: { xs: 1.25, sm: 1.75 },
      pl: { xs: 1.5, sm: 2 },
      mt: 0.65,
      mb: 0.35,
      borderLeft: `2px solid ${alpha(color, 0.35)}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.65
    }}
  >
    {children}
  </Box>
)

export const TreeNode = ({
  color,
  title,
  subtitle,
  trailing,
  leading,
  open,
  onToggle,
  expandable = true,
  depth = 1,
  children
}) => {
  const surface = depth === 0
    ? `linear-gradient(90deg, ${alpha(color, 0.62)} 0%, ${alpha(color, 0.22)} 38%, ${alpha('#000', 0.28)} 100%)`
    : alpha('#000', depth === 1 ? 0.22 : 0.16)

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onClick={expandable ? onToggle : undefined}
        onKeyDown={expandable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle?.()
              }
            }
          : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: depth === 0 ? 1.5 : 1.15,
          py: depth === 0 ? 1.15 : 0.85,
          borderRadius: depth === 0 ? 1.25 : 1,
          border: `1px solid ${alpha(color, depth === 0 ? 0.5 : 0.28)}`,
          borderLeft: `${depth === 0 ? 5 : 3}px solid ${color}`,
          background: surface,
          cursor: expandable ? 'pointer' : 'default',
          transition: 'border-color 140ms ease, background-color 140ms ease',
          '&:hover': expandable
            ? { borderColor: alpha(color, 0.7) }
            : undefined
        }}
      >
        {leading || null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: depth === 0 ? '0.98rem' : depth === 1 ? '0.86rem' : '0.8rem',
              color: alpha('#fff', 0.96),
              lineHeight: 1.2
            }}
            noWrap
          >
            {title}
          </Typography>
          {subtitle
            ? (
              <Typography
                variant='caption'
                sx={{
                  color: alpha('#fff', depth === 0 ? 0.82 : 0.7),
                  display: 'block',
                  mt: 0.35,
                  fontWeight: depth === 0 ? 700 : 600,
                  fontSize: depth === 0 ? '0.78rem' : '0.72rem',
                  letterSpacing: 0.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {subtitle}
              </Typography>
              )
            : null}
        </Box>
        {trailing
          ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', flexShrink: 0, maxWidth: { xs: '48%', sm: 'none' } }}>
              {trailing}
            </Box>
            )
          : null}
        {expandable
          ? (
            <ExpandMoreIcon
              sx={{
                color: alpha('#fff', 0.65),
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 160ms ease',
                flexShrink: 0,
                fontSize: depth === 0 ? 22 : 20
              }}
            />
            )
          : null}
      </Box>
      {expandable
        ? (
          <Collapse in={open} timeout='auto' unmountOnExit>
            <TreeChildren color={color}>{children}</TreeChildren>
          </Collapse>
          )
        : null}
    </Box>
  )
}
